/**
 * "Where did you hear about us?" as an in-app prompt.
 *
 * Owner decision 2026-10-04: the survey left the end of onboarding ("it should
 * be inside, after he clicks the CV inside My Tabbio"). The question is asked
 * ONCE per account, on the CV screen, right after a CV was opened from the My
 * Tabbio hub. Three facts decide whether it shows, all kept here so the hook
 * (useWhereHeardPrompt) stays thin and this logic stays testable:
 *
 * 1. The REQUEST: My Tabbio stamps `requestWhereHeardPrompt()` right before it
 *    pushes a CV route; the CV screen reads the stamp on mount. A stamp older
 *    than the TTL is ignored, so a CV reached any other way (deep link, chat
 *    card, drawer) never shows the prompt. The stamp is module state on
 *    purpose: no route param, so the URL stays clean and a reload shows nothing.
 * 2. The LOCAL "asked" flag, per account: written the moment the question is
 *    answered or skipped, read synchronously before anything else, so the
 *    common case (already asked) costs no network call.
 * 3. The SERVER answer: `whereHeard` / `whereHeardAskedAt` from
 *    GET /api/onboarding/state (sectionSettings.onboarding, the same place
 *    the onboarding step wrote to), so an answer or a skip on one device, or
 *    one given during onboarding before this change, is honoured everywhere.
 */
import { persistPreference, readPersistedPreference } from "./app-preferences-storage";
import { createSecureStoreKey } from "./secure-store-key";

const ASKED_STORAGE_PREFIX = "tabbio_where_heard_asked_v1";

/** How long a My Tabbio request stays valid for the CV screen to pick up. */
export const WHERE_HEARD_PROMPT_REQUEST_TTL_MS = 15_000;

/** Let the push transition land before the sheet rises over the CV. */
export const WHERE_HEARD_PROMPT_OPEN_DELAY_MS = 650;

let requestedAt: number | null = null;

/** Called by My Tabbio right before it opens one of the user's own CVs. */
export function requestWhereHeardPrompt(now: number = Date.now()) {
  requestedAt = now;
}

/** True while a fresh My Tabbio request is waiting for a CV screen. */
export function hasPendingWhereHeardPromptRequest(now: number = Date.now()) {
  if (requestedAt === null) return false;
  const age = now - requestedAt;
  return age >= 0 && age <= WHERE_HEARD_PROMPT_REQUEST_TTL_MS;
}

/** The CV screen took the request (it showed the prompt or decided not to). */
export function clearWhereHeardPromptRequest() {
  requestedAt = null;
}

function askedStorageKey(accountId: string) {
  return createSecureStoreKey(ASKED_STORAGE_PREFIX, accountId);
}

/** Per-account: was the question already put to this account on this device? */
export function hasWhereHeardBeenAsked(accountId: string | null | undefined) {
  // No signed-in account: nothing to attribute, never ask.
  if (!accountId) return true;
  return readPersistedPreference(askedStorageKey(accountId)) === "1";
}

export function markWhereHeardAsked(accountId: string | null | undefined) {
  if (!accountId) return;
  persistPreference(askedStorageKey(accountId), "1");
}

/** The two fields GET /api/onboarding/state returns for this question. */
export interface WhereHeardServerState {
  whereHeard?: string | null;
  whereHeardAskedAt?: string | null;
}

/** Answered (any value) or skipped (asked-at only) on some device already. */
export function wasWhereHeardAskedOnServer(
  state: WhereHeardServerState | null | undefined,
) {
  return Boolean(state?.whereHeard || state?.whereHeardAskedAt);
}

/**
 * The whole decision, given the three facts above. `serverState` is `undefined`
 * while the server has not answered yet (the caller waits), `null` when the
 * check failed (fail quiet: a flaky network must never make the prompt nag).
 */
export function shouldShowWhereHeardPrompt(input: {
  requested: boolean;
  accountId: string | null | undefined;
  askedLocally: boolean;
  serverState: WhereHeardServerState | null | undefined;
}): boolean {
  if (!input.requested || !input.accountId || input.askedLocally) return false;
  if (input.serverState === undefined || input.serverState === null) return false;
  return !wasWhereHeardAskedOnServer(input.serverState);
}
